import { useEffect, useMemo } from 'react';
import { MarketStatus } from '@sportspredict/shared';
import { useGetSecondChanceMarketsQuery, useGetSecondChanceTradesQuery } from '@/store/api';

export function useSecondChanceD2({
	lobby,
	user,
	markets,
	userTrades,
	isUserRevived,
	isUserFinallyEliminated,
	setIsSecondChanceResultOpen,
}: {
	lobby: any;
	user: any;
	markets: any[];
	userTrades: any[];
	isUserRevived: boolean;
	isUserFinallyEliminated: boolean;
	setIsSecondChanceResultOpen: (value: boolean) => void;
}) {
	const lobbyId = lobby?.id;

	const { data: secondChanceMarketsData, refetch: refetchSecondChanceMarkets } =
		useGetSecondChanceMarketsQuery(lobbyId, {
			skip: !lobbyId,
		});
	const isSecondChanceD2 = (secondChanceMarketsData?.length ?? 0) > 0;

	const { data: userSecondChanceTrades = [] } = useGetSecondChanceTradesQuery(
		{ lobbyId },
		{ skip: !lobbyId || !isSecondChanceD2 }
	);

	const secondChanceMarkets = useMemo(() => {
		if (isSecondChanceD2) {
			return [...(secondChanceMarketsData || [])].sort(
				(a: any, b: any) => (a.priority ?? 0) - (b.priority ?? 0)
			);
		}
		return markets
			.filter((m: any) => m.is_second_chance)
			.sort((a: any, b: any) => (a.second_chance_priority ?? 0) - (b.second_chance_priority ?? 0));
	}, [markets, secondChanceMarketsData, isSecondChanceD2]);

	const availableSecondChanceMarketsCount = useMemo(() => {
		if (!secondChanceMarkets?.length) return 0;

		if (isSecondChanceD2) {
			// D2 markets from separate API — filter by open status and untraded
			return secondChanceMarkets.filter((market: any) => {
				if (market.status !== MarketStatus.Open) return false;
				if (!userSecondChanceTrades?.length) return true;
				return !userSecondChanceTrades.some(
					(t: any) => String(t.second_chance_market_id) === String(market.id)
				);
			}).length;
		}

		return secondChanceMarkets.filter((market: any) => {
			if (market.status !== MarketStatus.SecondChance) return false;
			if (!userTrades?.length) return true;
			return !userTrades.some((t: any) => String(t.market_id ?? t.marketId) === String(market.id));
		}).length;
	}, [secondChanceMarkets, userTrades, userSecondChanceTrades, isSecondChanceD2]);

	const shouldOpenSecondChancePopup =
		lobby?.is_revival_initiated && (isUserRevived || isUserFinallyEliminated);

	useEffect(() => {
		if (!shouldOpenSecondChancePopup) return;

		const key = `revivalSeen:${lobby.id}:${user?.id}`;
		if (localStorage.getItem(key)) return;

		refetchSecondChanceMarkets().then(() => {
			setIsSecondChanceResultOpen(true);
			localStorage.setItem(key, 'true');
		});
	}, [
		shouldOpenSecondChancePopup,
		lobby?.id,
		user?.id,
		isUserRevived,
		isUserFinallyEliminated,
		refetchSecondChanceMarkets,
		setIsSecondChanceResultOpen,
	]);

	return {
		lobbyId,
		secondChanceMarketsData,
		refetchSecondChanceMarkets,
		isSecondChanceD2,
		userSecondChanceTrades,
		secondChanceMarkets,
		availableSecondChanceMarketsCount,
	};
}
